import { useCallback, useState } from 'react';
import { AgenciesService } from '@services/requests/housien-services/AgenciesService';

export interface UseRevertCancellationResult {
  keep: () => Promise<void>;
  isKeeping: boolean;
  isError: boolean;
}

/**
 * Provides a keep() action for a subscription whose cancellation was scheduled.
 *
 * Calls POST /agencies/{agencyId}/subscription/revert-cancellation. The page
 * refetches the subscription through onKept, so the screen shows what the
 * server now says rather than an optimistic guess.
 */
export function useRevertCancellation(
  agencyId: string,
  onKept?: () => void,
): UseRevertCancellationResult {
  const [isKeeping, setIsKeeping] = useState(false);
  const [isError, setIsError] = useState(false);

  const keep = useCallback(async () => {
    setIsKeeping(true);
    setIsError(false);
    try {
      await AgenciesService.revertCancellation(agencyId);
      onKept?.();
    } catch {
      setIsError(true);
    } finally {
      setIsKeeping(false);
    }
  }, [agencyId, onKept]);

  return { keep, isKeeping, isError };
}
